import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faSearch } from '@fortawesome/pro-light-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import smsLogo from '../../../static/images/logos/logo.png';
import cn from 'classnames';
import './ShopPageHeader.scss';

import { openCuratorGroupModal, focusShopSearch, openNewCircleModal } from '../../../Actions/UIActions';
import { updateCurrentUser } from '../../../Actions/UserActions';
import { createCuratorGroupFavorite, deleteCuratorGroupFavorite, updateCuratorGroup } from '../../../Actions/ShopActions';
import {
  getCuratorGroupsIncludingUserId,
  getCuratorGroupFavorites,
  isCuratorGroupIdMine,
  getUserId,
  hasProductIdsPromoted,
  isShopper,
  getCuratorGroups
} from '../../../Helpers/user_helpers';
import { isAnalyticsMode } from '../../../Helpers/ui_helpers';
import { getPreviewImageForGroup, getLinkToPrivateCuratorGroupPage, getLinkToPublicCuratorGroupPage } from '../../../Helpers/shop_helpers';
import { getSocialIconsForArtist } from '../../../Helpers/social_helpers';
import { tooltipKeys } from '../../../Helpers/onboarding_helpers';
import SmartImage from '../../General/SmartImage';
import Tooltip from '../../General/Tooltip';
import TrainingTooltip from '../../General/TrainingTooltip';
import Loader from '../../Loader/Loader';

import ShopProfileActions from './ShopProfileActions';
import ShopPageHeaderImage from './ShopPageHeaderImage';

const ShopPageHeader = props => {
  const { user, activeRefinementObjects, isUnfilteredState, query } = props;
  const { activeCurator, activeCuratorGroup, activeCategory, activeDepartment, activeIndustry, activeBrands } = activeRefinementObjects;
  const curatorGroupsForActiveCurator = activeCurator ? getCuratorGroupsIncludingUserId(user, activeCurator.id) : [];
  const curatorGroupFavorite = activeCuratorGroup ? getCuratorGroupFavorites(user)?.find(f => f.CuratorGroup_id === activeCuratorGroup.id) : null;
  const isMyShop = activeCurator && activeCurator.id === getUserId(user);

  // Header Data
  let name, bio;
  if (activeCurator) {
    name = activeCurator.name;
    bio = activeCurator.description;
  } else if (activeCuratorGroup) {
    name = activeCuratorGroup.title;
  } else if (props.Curator_id || props.Curator_username || props.CuratorGroup_id) {
    name = '';
  } else {
    name = 'All ShopMy curators';
  }

  /*
    Favoriting Logic
  */
  const isMyGroup = activeCuratorGroup && isCuratorGroupIdMine(user, activeCuratorGroup.id);
  const [isFavoriting, setIsFavoriting] = React.useState(false);
  const hasCuratorGroups = getCuratorGroups(user).length > 0;
  const isFavorited = curatorGroupsForActiveCurator.length > 0 || !!curatorGroupFavorite;

  const pressHeart = e => {
    e.stopPropagation();
    if (isMyGroup) return;

    window.__ADD_EVENT__('Shop - Clicked Heart', {
      Curator: activeCurator?.name || 'None',
      'Curator Group': activeCuratorGroup?.title || 'None',
      'Has Curator Groups': hasCuratorGroups
    });

    if (activeCurator) {
      if (hasCuratorGroups) {
        // If they have circles, we want to open the add curator modal for quick addition
        props.openCuratorGroupModal({
          source: 'Shop Header',
          params: {
            type: 'add',
            curator: activeCurator,
            curatorGroup: activeCuratorGroup
          }
        });
      } else {
        // If they don't have any circles, we want to open the create circle modal
        props.openNewCircleModal({
          params: {
            panel: 'final',
            initial_curators: [activeCurator]
          }
        });
      }
    } else if (activeCuratorGroup) {
      // If they have circles, we want to allow them to favorite or unfavorite the circle
      if (isFavorited) {
        props.deleteCuratorGroupFavorite(curatorGroupFavorite, { source: 'shop_header' }).then(() => {
          window.ALERT.success(`Unfavorited the shopping circle "${activeCuratorGroup.title}"`);
        });
      } else {
        if (isFavoriting) return;
        setIsFavoriting(true);
        props
          .createCuratorGroupFavorite(activeCuratorGroup, { source: 'shop_header' })
          .then(resp => {
            resp.groupFavorite && window.ALERT.success(`Favorited the shopping circle "${activeCuratorGroup.title}"`);
          })
          .finally(() => {
            setIsFavoriting(false);
          });
      }
    }
  };

  /*
    Circle Management
  */
  const clickToAddCuratorsToCircle = () => {
    props.openNewCircleModal({
      params: {
        panel: 'intro',
        initial_group: activeCuratorGroup
      }
    });
  };

  /*
    Tab Handling
  */
  const hasSearchQuery = query && query.length > 0;
  const [activeTabStyle, setActiveTabStyle] = React.useState({ left: 0, width: 0 });
  const tabsRef = React.useRef([]);
  const tabOptions = [
    {
      value: `foryou`,
      label: 'For You',
      mobileLabel: 'For You',
      sublabel: 'AI powered recommendations.',
      isHidden: isMyShop,
      isDisabled: hasSearchQuery || isUnfilteredState || !hasProductIdsPromoted(user),
      isDisabledWarning: hasSearchQuery
        ? 'Not available when searching, clear search pill to reset.'
        : isUnfilteredState
        ? 'Please select a category'
        : !hasProductIdsPromoted(user)
        ? 'Heart a few products to get personalized recommendations'
        : null
    },
    {
      value: `latest`,
      label: 'Latest Finds',
      mobileLabel: 'Latest',
      sublabel: 'Newly discovered.',
      isDisabled: hasSearchQuery || isUnfilteredState,
      isDisabledWarning: hasSearchQuery
        ? 'Not available when searching, clear search pill to reset.'
        : isUnfilteredState
        ? 'Please select a category'
        : null
    },
    {
      value: `popular`,
      // label: props.isCuratedShop ? 'By Category' : 'Most Popular',
      label: 'Most Popular',
      mobileLabel: 'Popular',
      sublabel: 'Most talked about products.',
      isHidden: isShopper(user) && isMyShop,
      isDisabled: hasSearchQuery || isUnfilteredState,
      isDisabledWarning: hasSearchQuery
        ? 'Not available when searching, clear search pill to reset.'
        : isUnfilteredState
        ? 'Please select a category'
        : null
    },
    {
      value: `collections`,
      label: isMyShop && isShopper(user) ? 'Wishlists' : 'Collections',
      mobileLabel: isMyShop && isShopper(user) ? 'Wishlists' : 'Collections',
      sublabel: 'Curated Lists.',
      isDisabled: hasSearchQuery || (!activeCuratorGroup && !activeCurator),
      isDisabledWarning: hasSearchQuery
        ? 'Collections are not available when searching.'
        : isUnfilteredState
        ? 'Please select a category'
        : activeCuratorGroup
        ? "Top Lists don't have collections."
        : activeCurator
        ? null
        : 'Filter to specific curator shops to see all recent collections'
    },
    {
      value: 'consults',
      label: 'Consults',
      mobileLabel: 'Consults',
      sublabel: '1 on 1 consultations.',
      isHidden: !activeCurator || !activeCurator.showConsultsTabInShop,
      isDisabled: hasSearchQuery || isUnfilteredState,
      isDisabledWarning: hasSearchQuery ? 'Consults are not available when searching.' : isUnfilteredState ? 'Please select a category' : null
    }
  ]
    .filter(c => !c.isHidden)
    .map(tab => ({
      ...tab,
      onClick: () => {
        if (tab.isDisabled) {
          window.ALERT.warn(tab.isDisabledWarning);
          window.__ADD_EVENT__(`Shop - Clicked Disabled Tab`, {
            value: tab.value,
            Curator: activeCurator?.name || 'None',
            'Curator Group': activeCuratorGroup?.title || 'None'
          });
        } else {
          props.goToTab(tab.value);
          window.__ADD_EVENT__(`Shop - Clicked Tab`, {
            value: tab.value,
            Curator: activeCurator?.name || 'None',
            'Curator Group': activeCuratorGroup?.title || 'None'
          });
        }
      },
      isActive: tab.isActiveOverride || tab.value === props.tab
    }));
  const activeTabIndex = tabOptions.findIndex(t => t.isActive);
  React.useEffect(() => {
    if (tabsRef.current[activeTabIndex]) {
      const { offsetLeft, offsetWidth } = tabsRef.current[activeTabIndex];
      setActiveTabStyle({ left: offsetLeft, width: offsetWidth });
    }
  }, [activeTabIndex, tabOptions.length]);

  // Handle switching the curator group
  const openSwitchGroupModal = e => {
    e.stopPropagation();
    props.openCuratorGroupModal({
      params: {
        type: 'select',
        curator: activeCurator,
        curatorGroup: activeCuratorGroup
      }
    });
  };

  // UI for category
  let curatedByParts = [];

  // Add the category, department, or industry
  if (activeCategory) curatedByParts.push({ display: activeCategory.name, unrefine: () => props.clearCategoryId(activeCategory.id) });
  else if (activeDepartment) curatedByParts.push({ display: activeDepartment.name, unrefine: () => props.clearDepartmentId(activeDepartment.id) });
  else if (activeIndustry) curatedByParts.push({ display: activeIndustry.name, unrefine: () => props.clearIndustryId(activeIndustry.id) });
  else if (hasSearchQuery) curatedByParts.push({ display: query, unrefine: () => props.setQuery('') });

  // Add the brand
  if (activeBrands) {
    if (curatedByParts.length) curatedByParts.push({ display: ', by ' });
    curatedByParts.push({
      display: activeBrands.map(b => b.name).join(', '),
      unrefine: () => props.clearBrandIds(activeBrands.map(b => b.id))
    });
  }

  // Add the curated text
  curatedByParts.push({ display: curatedByParts.length ? ', curated by' : 'Curated by', unrefine: null });

  // UI Management
  const showAddToCircleTooltip = !hasCuratorGroups && !!activeCurator;
  const showShopActions = isMyShop || (activeCurator && window.__ADMIN_CONTROL_MODE__);
  const numViews = props.currentAnalytics?.shop_stats?.num_views;

  // Actions Management
  let actions = [];
  if (props.isCuratorGroupShop) {
    if (isMyGroup) {
      // Allow them to add new curators to the group
      actions.push({
        label: 'Add Curators',
        onClick: clickToAddCuratorsToCircle
      });
    } else if (isFavorited) {
      // Allow them to unfavorite the group
      actions.push({
        label: 'Unfavorite',
        onClick: pressHeart
      });
    } else {
      // Allow them to favorite the group
      actions.push({
        label: 'Favorite',
        onClick: pressHeart
      });
    }
  } else if (props.isCuratorShop) {
    if (!isFavorited && !isMyShop) {
      actions.push({
        label: 'Add to circle',
        onClick: pressHeart,
        showAddToCircleTooltip: showAddToCircleTooltip
      });
    }
  }

  // UI Management
  const canShowBio = bio || (props.isFetchingActiveRefinementObjects && props.isCuratorShop);

  // Class Management
  const isLoading = props.isFetchingActiveRefinementObjects && !activeCurator && !activeCuratorGroup;
  const additionalClasses = {
    loading: isLoading
  };

  return (
    <header className={cn('shop-page-header-container', additionalClasses)}>
      {showShopActions && <ShopProfileActions isMyShop={isMyShop} isFetchingAnalytics={props.isFetchingAnalytics} />}
      <div className={cn('main-panel', additionalClasses)}>
        <div className='header-data-section'>
          <div className='image-container'>
            <ShopPageHeaderImage activeRefinementObjects={props.activeRefinementObjects} isLoading={isLoading} />
          </div>
          <div className='curated-by-container'>
            {isAnalyticsMode(props.ui) && (
              <div className='views-count'>{props.isFetchingAnalytics ? '...' : `${numViews} shop view${numViews !== 1 ? 's' : ''}`}</div>
            )}
            <div className={cn('label-container', additionalClasses)}>
              <h2 className={cn('label', additionalClasses)}>
                {curatedByParts.map((part, idx) => {
                  return (
                    <span key={idx} className={cn('curated-by-part', { clickable: !!part.unrefine })} onClick={part.unrefine}>
                      {part.display}
                    </span>
                  );
                })}
              </h2>
            </div>
            <div className={cn('name-container', additionalClasses)}>
              <h1 className={cn('name', additionalClasses)}>{isLoading ? '...' : name}</h1>
            </div>
          </div>
          {canShowBio && (
            <div className={cn('bio-container', additionalClasses)}>
              <h3 className='bio'>{bio}</h3>
            </div>
          )}
          {activeCuratorGroup && (
            <Link to={getLinkToPublicCuratorGroupPage(activeCuratorGroup)} className='view-curators-container'>
              <div className='view-curators'>
                View {activeCuratorGroup.num_curators} curator{activeCuratorGroup.num_curators !== 1 ? 's' : ''}
              </div>
            </Link>
          )}
          {!!actions.length && (
            <div className={cn('actions-container', additionalClasses)}>
              {actions.map(action => {
                const wrapInTooltip = el => {
                  return action.showAddToCircleTooltip ? (
                    <TrainingTooltip
                      visible
                      trainingKey={tooltipKeys.ADD_TO_CIRCLE_FROM_SHOP_PAGE_HEADER}
                      fadeInDelay={2_000}
                      fadeOutDelay={10_000}
                      message={
                        <>
                          Favorite this user and add them to your <b>first circle</b>.
                        </>
                      }
                      numDismissalsToHide={window.__IS_PROD__ ? 3 : 1_000}
                      position='right'
                      positionIfInitialOffScreen='top'
                    >
                      {el}
                    </TrainingTooltip>
                  ) : (
                    el
                  );
                };
                return wrapInTooltip(
                  <div key={action.label} className={cn('action', additionalClasses, action.additionalClasses || '')} onClick={action.onClick}>
                    {action.label}
                  </div>
                );
              })}
            </div>
          )}
          {props.isCuratorShop && (
            <div className={cn('social-icons-container', additionalClasses)}>
              {activeCurator && getSocialIconsForArtist(activeCurator, { iconStyle: 'dark' })}
              {isFavorited && (
                <Tooltip message='In Your Circle'>
                  <div onClick={pressHeart} className='in-circle-container'>
                    <div className='in-circle'>
                      <FontAwesomeIcon icon={faHeartSolid} />
                    </div>
                  </div>
                </Tooltip>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

ShopPageHeader.propTypes = {
  // From Inside
  user: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired,
  openCuratorGroupModal: PropTypes.func.isRequired,
  createCuratorGroupFavorite: PropTypes.func.isRequired,
  deleteCuratorGroupFavorite: PropTypes.func.isRequired,
  updateCuratorGroup: PropTypes.func.isRequired,
  updateCurrentUser: PropTypes.func.isRequired,
  focusShopSearch: PropTypes.func.isRequired,
  openNewCircleModal: PropTypes.func.isRequired,

  // From Outside
  isFetchingProductRefinements: PropTypes.bool.isRequired,
  isFetchingActiveRefinementObjects: PropTypes.bool.isRequired,
  activeRefinementObjects: PropTypes.object.isRequired,
  clearCategoryId: PropTypes.func.isRequired,
  clearDepartmentId: PropTypes.func.isRequired,
  clearIndustryId: PropTypes.func.isRequired,
  isCuratorShop: PropTypes.bool.isRequired,
  currentAnalytics: PropTypes.object,
  isFetchingAnalytics: PropTypes.bool.isRequired
};

const mapStateToProps = state => {
  const { user, shop, ui } = state;
  return { user, shop, ui };
};

export default connect(mapStateToProps, {
  openCuratorGroupModal,
  createCuratorGroupFavorite,
  deleteCuratorGroupFavorite,
  updateCuratorGroup,
  updateCurrentUser,
  focusShopSearch,
  openNewCircleModal
})(ShopPageHeader);
